import { InputDateRange } from "#core/components/input/date/InputDateRange"
import { Box, Divider, Stack, Typography } from "@mui/material"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import VacationHolidaysCard from "./VacationHolidaysCard"
import { UseHolidays } from "./VacationLeaveDays"
/**
 * @typedef {object} GetDatesData
 * @property {dayjs.Dayjs} start - Fecha de inicio de la solicitud de vacaciones.
 * @property {dayjs.Dayjs} end - Fecha de fin de la solicitud de vacaciones.
 * @property {number} days_taken - Número de días que se han tomado.
 */
/**
 * Componente para gestionar la solicitud de vacaciones de un colaborador.
 *
 * @component
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.colaborador - Información del colaborador que solicita las vacaciones.
 * @param {(GetDatesData) => {}} props.setData - Retorna un objeto con las fechas de inicio y fin seleccionadas.
 * @param {number} [props.available_days=10] - Días disponibles para solicitar..
 *
 * @returns {JSX.Element} Elemento JSX que representa el formulario o interfaz de solicitud de vacaciones.
 *
 * @example
 * <VacationRequest
 *   colaborador={{ id: 1, nombre: "Juan Pérez" }}
 *   setData={{start, end,days_taken} => console.log(start, end,days_taken)}
 *   available_days={15}
 * />
 */
export default function VacationRequest({
	colaborador,
	setData,
	available_days,
	defaultValue = { start: null, end: null },
}) {
	const [dates, setDates] = useState(defaultValue)
	const holidays = UseHolidays(colaborador, dates.start, dates.end)

	const days_taken = useMemo(() => {
		if (!dates.start?.isValid() || !dates.end?.isValid()) return 0
		let days_taken = dates.end?.diff(dates.start, "day") + 1
		return days_taken - holidays.length
	}, [dates.start, dates.end, holidays.length])

	useEffect(() => {
		if (typeof setData === "function") {
			setData({ ...dates, days_taken })
		}
	}, [dates, days_taken, setData])

	return (
		<Box sx={{ width: "100%" }} display={"flex"} flexDirection={"column"} gap={2}>
			<Stack direction={"row"} spacing={3} justifyContent={"space-around"}>
				<TextValue text="Disponible" value={available_days} />
				<TextValue text="Solicitados" value={days_taken} />
			</Stack>
			<Divider />
			<InputDateRange
				onChange={setDates}
				defaultStart={dates.start}
				defaultEnd={dates.end}
			/>

			<VacationHolidaysCard holidays={holidays} />
		</Box>
	)
}

const TextValue = ({ text, value, variant = "h6" }) => {
	return (
		<Typography
			variant={variant}
			sx={{
				whiteSpace: "pre-line",
				textAlign: "center",
			}}
		>
			{text}
			{"\n"} {value}
		</Typography>
	)
}
